import { useState, useEffect, useContext } from "react";
import { Flex, App, Spin, notification, Button, Pagination } from "antd";

// import SearchFrom3 from "@/components/inputs/SearchFrom3";
import CustomSelect from "@/components/inputs/CustomSelect";
import NoticesSubscriptionsTable from "@/components/tables/NoticesSubscriptionsTable";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getSubscribes, unsubscribe } from "@/api/notices";

const LOCAL_STORAGE_KEY = "notices_subscriptions";

const SubscriptionsTab = ({ type }) => {
  const { message } = App.useApp();
  const { user } = useContext(UserContext);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [optionsPending, setOptionsPending] = useState(false);
  const [subscriptionsTypes, setSubscriptionsTypes] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const initialTableParams = {
    pagination: {
      current: 1,
      pageSize: 10,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
    filters: {
      search: null,
      type: null,
    },
    sort: "-created_at",
  };

  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  //  initial fetch
  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (type) {
      updateFilters(tableParams, true);
    }

    // eslint-disable-next-line
  }, [type]);

  const fetchOptions = async () => {
    setOptionsPending(true);

    try {
      const types = [
        { name: "Объект", type: "object" },
        { name: "Задача", type: "task" },
      ];

      setSubscriptionsTypes(types);

      // Инициализация параметров из localStorage
      const localParams =
        JSON.parse(
          localStorage.getItem(`${user.user_uuid}_${LOCAL_STORAGE_KEY}`)
        ) || initialTableParams;

      setTableParams(localParams);
      fetchData(localParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  };

  const fetchData = async (tParams) => {
    setIsLoading(true);

    const currentParams = tParams || tableParams;
    const params = {
      page: currentParams.pagination.current,
      perpage: currentParams.pagination.pageSize,
      sort: currentParams.sort,
      ...currentParams.filters,
      user: user.user_uuid,
    };

    try {
      const { subscriptions, total, page, perPage } =
        await getSubscribes(params);
      setData(subscriptions);

      const currentParams = tParams || tableParams;
      const updatedTableParams = {
        ...currentParams,
        pagination: {
          ...currentParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setParams(updatedTableParams);
      setSelectedRowKeys([]);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setTableParams(params);

    // Сохраняем параметры пагинации в localStorage
    localStorage.setItem(
      `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
      JSON.stringify(params)
    );
  };

  const updateFilters = (localParams, newFilters, resetPage = false) => {
    const updatedFilters = { ...localParams.filters, ...newFilters };

    const updatedTableParams = {
      ...localParams,
      filters: updatedFilters,
      pagination: {
        ...localParams.pagination,
        current: resetPage ? 1 : localParams.pagination.current,
      },
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Обработчик для смены фильтров
  const handleFilterChange = async (name, val) => {
    updateFilters(
      tableParams,
      {
        [name]: val || null,
      },
      true
    );
  };

  // Обработчик изменения пагинации или сортировки, которые приходят из таблицы
  const handleTableChange = (pagination, filters, sorter) => {
    const isSortChanged =
      `${sorter.order === "descend" ? "-" : ""}${sorter.field || ""}` !==
      tableParams.sort;

    const updatedTableParams = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: isSortChanged
          ? 1
          : pagination.current || tableParams.pagination.current,
        pageSize: pagination.pageSize || tableParams.pagination.pageSize,
      },
      sort: `${sorter.order === "descend" ? "-" : ""}${sorter.field || ""}`,
    };
    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  const handlePaginationChange = (current, pageSize) => {
    if (pageSize !== tableParams.pagination.pageSize) {
      setSelectedRowKeys([]);
    }
    const updatedTableParams = {
      ...tableParams,

      pagination: {
        ...tableParams.pagination,
        current,
        pageSize,
      },
    };
    setParams(updatedTableParams);

    fetchData(updatedTableParams);
  };

  const showUnsubNotice = (count, name = "") => {
    const title =
      count === 1
        ? `Подписка отменена: ${name || "Объект"}`
        : `Подписка отменена: Несколько объектов`;

    notification.open({
      message: title,
      description:
        count === 1
          ? "Вы отписались от уведомлений на объект раздела."
          : "Вы отписались от уведомлений на несколько объектов раздела.",
      icon: null,
      duration: 4,
      pauseOnHover: true,
      placement: "bottomRight",
      style: {
        border: "1px solid #91CAFF",
        background: "#e6f4ff",
        borderRadius: 8,
        width: 500,
      },
    });
  };

  const handleUnsubscribe = async () => {
    if (!selectedRowKeys.length) return;

    try {
      await unsubscribe({
        items: selectedRowKeys,
        user: user.user_uuid,
      });

      await fetchData(tableParams);

      if (selectedRowKeys.length === 1) {
        const one = data.find((r) => r.uuid === selectedRowKeys[0]);
        const name = one?.info?.object_name || "";
        showUnsubNotice(1, name);
      } else {
        showUnsubNotice(selectedRowKeys.length);
      }
      setSelectedRowKeys([]);
    } catch (error) {
      message.open({ type: "error", content: error.message, duration: 10 });
    }
  };

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <Flex gap={15} vertical className="h-100">
          <Flex justify="space-between" align="center">
            <Flex gap={8} align="center">
              {/* <SearchFrom3
                placeholder="Поиск"
                defaultValue={tableParams.filters.search}
                onSearch={(search) => handleFilterChange("search", search)}
              /> */}
              <CustomSelect
                placeholder="Раздел"
                options={subscriptionsTypes}
                fieldNames={{
                  label: "name",
                  value: "type",
                  key: "type",
                }}
                style={{
                  width: 100,
                }}
                showSearch
                optionFilterProp="name"
                allowClear
                placement="bottomRight"
                popupMatchSelectWidth={false}
                value={tableParams.filters.type}
                onChange={(type) => handleFilterChange("type", type)}
              />
            </Flex>
            {selectedRowKeys.length > 0 && (
              <Button
                type="primary"
                disabled={!selectedRowKeys.length}
                onClick={handleUnsubscribe}
              >
                Отписаться
              </Button>
            )}
          </Flex>
          <div
            ref={tableContainerRef}
            style={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <NoticesSubscriptionsTable
              data={data}
              loading={isLoading}
              pagination={false}
              onChange={handleTableChange}
              showSelect
              selectedRowKeys={selectedRowKeys}
              onRowsSelect={(selectedRowKeys) =>
                setSelectedRowKeys(selectedRowKeys)
              }
              scroll={{ x: "100%", y: tableHeight - headerHeight }}
            />
          </div>

          <div style={{ minHeight: 32 }}>
            <Pagination
              {...tableParams.pagination}
              onChange={handlePaginationChange}
            />
          </div>
        </Flex>
      )}
    </>
  );
};

export default SubscriptionsTab;
